import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Users,
  Inbox,
  CalendarDays,
  ShieldCheck,
  LogOut,
  Check,
  X,
  Trash2,
  UserPlus,
  Power,
  Loader2,
  Save,
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * HR Dashboard — /hr
 *  Auth: PIN unlock via /api/master/unlock. Only owner / hr_manager / hr_approver
 *        can access. hr_only=true users have no other UI available.
 *  Tabs: Requests · Balances · Employees · Approvers (owner-only)
 */
export default function HRPage() {
  const [locked, setLocked] = useState(true);
  const [pin, setPin] = useState("");
  const [actor, setActor] = useState(null); // {name, code, hr_role, tier}
  const [tab, setTab] = useState("requests");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const unlockWith = async (code) => {
    const clean = (code || "").trim();
    if (!clean) return;
    try {
      const res = await axios.post(`${API}/master/unlock`, { password: clean });
      if (!res.data.hr_role || res.data.hr_role === "none") {
        toast.error("This account has no HR access.");
        return;
      }
      setActor({
        name: res.data.master_name,
        code: res.data.manager_code || clean,
        hr_role: res.data.hr_role,
        tier: res.data.tier,
        hr_only: !!res.data.hr_only,
      });
      setLocked(false);
      toast.success(`Welcome, ${res.data.master_name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid PIN");
    }
  };
  const unlock = () => unlockWith(pin);

  // Auto-unlock when ?pin=XXXX is in the URL (bookmarkable HR link)
  useEffect(() => {
    const preset = searchParams.get("pin");
    if (preset) {
      setPin(preset);
      unlockWith(preset);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const logout = () => {
    setActor(null);
    setLocked(true);
    setPin("");
    navigate("/paypunch");
  };

  if (locked) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black flex items-center justify-center px-4">
        <Card className="bg-[#1a1a1c] border-[#2a2a2c] p-8 w-full max-w-sm" data-testid="hr-lock-card">
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 rounded-full bg-blue-500/20 flex items-center justify-center">
              <ShieldCheck className="w-7 h-7 text-blue-300" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white text-center mb-1">HR Access</h1>
          <p className="text-gray-400 text-sm text-center mb-6">
            Enter your PIN or employee code
          </p>
          <Input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && unlock()}
            placeholder="PIN"
            className="bg-[#252527] border-[#3a3a3c] text-white text-center text-xl tracking-widest h-12 mb-3"
            data-testid="hr-pin-input"
            autoFocus
          />
          <Button
            onClick={unlock}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white h-11"
            data-testid="hr-unlock-btn"
          >
            Unlock
          </Button>
        </Card>
      </div>
    );
  }

  const isOwner = actor.hr_role === "owner";
  const canManage = ["owner", "hr_manager"].includes(actor.hr_role);

  const TABS = [
    { id: "requests", label: "Requests", icon: Inbox },
    { id: "calendar", label: "Calendar", icon: CalendarIcon },
    { id: "balances", label: "Balances", icon: CalendarDays },
    ...(canManage ? [{ id: "employees", label: "Employees", icon: Users }] : []),
    ...(isOwner ? [{ id: "approvers", label: "Approvers", icon: ShieldCheck }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#0f0f10] text-white">
      {/* Top bar */}
      <div className="border-b border-[#2a2a2c] bg-[#1a1a1c] px-4 py-3 flex items-center justify-between">
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-widest">HR Dashboard</div>
          <div className="text-lg font-semibold">
            {actor.name}
            <Badge className="ml-2 bg-blue-500/20 text-blue-300 border-0 text-[10px]">
              {actor.hr_role.replace("_", " ").toUpperCase()}
            </Badge>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={logout}
          className="text-gray-400 hover:text-white"
          data-testid="hr-logout-btn"
        >
          <LogOut className="w-4 h-4 mr-1" /> Sign out
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b border-[#2a2a2c] bg-[#141416] flex overflow-x-auto no-scrollbar">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                ${tab === t.id ? "border-blue-500 text-white" : "border-transparent text-gray-500 hover:text-gray-300"}`}
              data-testid={`hr-tab-${t.id}`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="p-4 sm:p-6 max-w-6xl mx-auto">
        {tab === "requests" && <RequestsTab actor={actor} />}
        {tab === "calendar" && <CalendarTab />}
        {tab === "balances" && <BalancesTab actor={actor} canEdit={canManage} />}
        {tab === "employees" && canManage && <EmployeesTab actor={actor} />}
        {tab === "approvers" && isOwner && <ApproversTab actor={actor} />}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// REQUESTS TAB — pending inbox with approve/deny
// ═══════════════════════════════════════════════════════════
function RequestsTab({ actor }) {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/hr/requests`, {
        params: { status, actor_code: actor.code },
      });
      setRows(res.data.requests || []);
    } catch (e) {
      toast.error("Failed to load requests");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const approve = async (id, overridePaid = null) => {
    setBusy(true);
    try {
      await axios.post(`${API}/hr/requests/${id}/approve`, {
        approver_code: actor.code,
        paid: overridePaid,
      });
      toast.success("Approved");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    } finally {
      setBusy(false);
    }
  };

  const deny = async (id) => {
    const reason = window.prompt("Reason for denial (optional):") || "";
    setBusy(true);
    try {
      await axios.post(`${API}/hr/requests/${id}/deny`, {
        approver_code: actor.code,
        reason,
      });
      toast.success("Denied");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Deny failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="flex gap-2 mb-4">
        {["pending", "approved", "denied", "all"].map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors
              ${status === s ? "bg-white text-black" : "bg-[#252527] text-gray-400 hover:bg-[#2f2f31]"}`}
            data-testid={`filter-${s}`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {rows.length === 0 && (
        <Card className="bg-[#1a1a1c] border-[#2a2a2c] p-10 text-center text-gray-500 text-sm">
          No {status === "all" ? "" : status} requests
        </Card>
      )}

      <div className="space-y-2">
        {rows.map((r) => (
          <Card
            key={r.id}
            className="bg-[#1a1a1c] border-[#2a2a2c] p-4"
            data-testid={`req-${r.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-semibold">{r.employee_name}</span>
                  <span className="text-xs text-gray-500 font-mono">#{r.employee_code}</span>
                  <TypeBadge type={r.request_type} />
                  <StatusBadge status={r.status} />
                </div>
                <div className="text-sm text-gray-400">
                  {r.start_date} → {r.end_date} · <span className="text-white">{r.days} days</span>
                </div>
                {r.reason && <div className="text-xs text-gray-500 mt-1">"{r.reason}"</div>}
                {r.status !== "pending" && r.approved_by_name && (
                  <div className="text-[11px] text-gray-500 mt-1">
                    {r.status === "approved" ? "✓" : "✗"} {r.approved_by_name} · {new Date(r.approved_at).toLocaleDateString()}
                    {r.denied_reason && ` — ${r.denied_reason}`}
                  </div>
                )}
              </div>
              {r.status === "pending" && (
                <div className="flex gap-1 shrink-0">
                  <Button
                    size="sm"
                    disabled={busy}
                    onClick={() => approve(r.id)}
                    className="bg-green-500 hover:bg-green-600 text-white h-9"
                    data-testid={`approve-${r.id}`}
                  >
                    <Check className="w-4 h-4 mr-1" /> Approve
                  </Button>
                  <Button
                    size="sm"
                    disabled={busy}
                    variant="outline"
                    onClick={() => deny(r.id)}
                    className="bg-[#252527] border-red-500/30 text-red-300 hover:bg-red-500/10 h-9"
                    data-testid={`deny-${r.id}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════
// BALANCES TAB — everyone's 2026 PTO summary
// ═══════════════════════════════════════════════════════════
function BalancesTab({ actor, canEdit }) {
  const [rows, setRows] = useState([]);
  // editing: { code, field: 'granted'|'taken', value }
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/hr/balances`);
      setRows(res.data.balances || []);
    } catch {
      toast.error("Failed to load balances");
    }
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!editing) return;
    try {
      const body = { actor_code: actor.code };
      if (editing.field === "granted") body.weeks_2026 = parseFloat(editing.value) || 0;
      if (editing.field === "taken") body.weeks_taken_manual = parseFloat(editing.value) || 0;
      await axios.put(`${API}/hr/balances/${editing.code}`, body);
      toast.success("Saved");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const r of rows) (g[r.department] = g[r.department] || []).push(r);
    return g;
  }, [rows]);

  const isEditingCell = (code, field) => editing && editing.code === code && editing.field === field;

  return (
    <>
      <div className="text-xs text-gray-500 mb-3">
        {rows.length} employees · Managers enter <em>Granted</em> and <em>Taken</em> manually. Taken values override auto-calc.
      </div>

      {Object.entries(grouped).map(([dept, list]) => (
        <div key={dept} className="mb-5">
          <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-2 px-1">
            {dept} ({list.length})
          </div>
          <Card className="bg-[#1a1a1c] border-[#2a2a2c] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#141416] text-[11px] uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2 w-16">Code</th>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-right px-3 py-2">Granted</th>
                  <th className="text-right px-3 py-2">Taken</th>
                  <th className="text-right px-3 py-2">Remaining</th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.code} className="border-t border-[#2a2a2c]" data-testid={`bal-${r.code}`}>
                    <td className="px-3 py-2 font-mono text-xs text-gray-400">{r.code}</td>
                    <td className="px-3 py-2 font-medium text-white">{r.name}</td>

                    {/* GRANTED */}
                    <td className="px-3 py-2 text-right">
                      {isEditingCell(r.code, "granted") ? (
                        <div className="flex justify-end gap-1">
                          <Input
                            type="number"
                            step="0.5"
                            value={editing.value}
                            onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                            onKeyDown={(e) => e.key === "Enter" && save()}
                            className="w-20 h-7 bg-[#252527] border-[#3a3a3c] text-white text-right"
                            data-testid={`edit-granted-${r.code}`}
                            autoFocus
                          />
                          <Button size="sm" onClick={save} className="h-7 bg-blue-500 hover:bg-blue-600 text-white px-2" data-testid={`save-granted-${r.code}`}>
                            <Save className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <button
                          onClick={() => canEdit && setEditing({ code: r.code, field: "granted", value: String(r.weeks_granted) })}
                          className={`text-white ${canEdit ? "hover:bg-blue-500/10 rounded px-1.5 py-0.5 cursor-pointer" : "cursor-default"}`}
                          data-testid={`click-granted-${r.code}`}
                          disabled={!canEdit}
                        >
                          {r.weeks_granted}w
                        </button>
                      )}
                    </td>

                    {/* TAKEN */}
                    <td className="px-3 py-2 text-right">
                      {isEditingCell(r.code, "taken") ? (
                        <div className="flex justify-end gap-1">
                          <Input
                            type="number"
                            step="0.5"
                            value={editing.value}
                            onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                            onKeyDown={(e) => e.key === "Enter" && save()}
                            className="w-20 h-7 bg-[#252527] border-[#3a3a3c] text-white text-right"
                            data-testid={`edit-taken-${r.code}`}
                            autoFocus
                          />
                          <Button size="sm" onClick={save} className="h-7 bg-blue-500 hover:bg-blue-600 text-white px-2" data-testid={`save-taken-${r.code}`}>
                            <Save className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <button
                          onClick={() => canEdit && setEditing({ code: r.code, field: "taken", value: String(r.weeks_taken) })}
                          className={`${canEdit ? "hover:bg-blue-500/10 rounded px-1.5 py-0.5 cursor-pointer" : ""} ${r.taken_is_manual ? "text-amber-300" : "text-gray-400"}`}
                          data-testid={`click-taken-${r.code}`}
                          title={r.taken_is_manual ? "Manually entered" : "Auto-calculated from approved paid requests"}
                          disabled={!canEdit}
                        >
                          {r.weeks_taken}w
                        </button>
                      )}
                    </td>

                    <td className={`px-3 py-2 text-right font-semibold ${r.weeks_remaining < 0 ? "text-red-400" : "text-green-400"}`}>
                      {r.weeks_remaining}w
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      ))}
    </>
  );
}

// ═══════════════════════════════════════════════════════════
// EMPLOYEES TAB — add / activate / inactivate / delete
// ═══════════════════════════════════════════════════════════
function EmployeesTab({ actor }) {
  const isOwner = actor.hr_role === "owner";
  const [rows, setRows] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    code: "", name: "", department: "Other", pay_type: "hourly",
    hourly_rate: 15, weekly_salary: 0, pay_category: "payroll",
  });

  const load = async () => {
    try {
      const res = await axios.get(`${API}/timeclock/employees/all`, {
        params: { actor_code: actor.code },
      });
      setRows(res.data || []);
    } catch {
      toast.error("Failed to load employees");
    }
  };
  useEffect(() => {
    load();
  }, []);

  const filtered = rows
    .filter((e) => !e.is_test)
    .filter((e) => showInactive || e.active)
    .filter((e) => {
      const q = search.toLowerCase();
      return !q || e.name.toLowerCase().includes(q) || (e.code || "").includes(q);
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const toggleActive = async (code, next) => {
    setBusy(true);
    try {
      await axios.post(`${API}/hr/employees/${code}/activate`, {
        active: next,
        actor_code: actor.code,
      });
      toast.success(next ? "Activated" : "Inactivated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteEmp = async (code, name) => {
    if (!window.confirm(`Permanently delete ${name}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/hr/employees/${code}`, { params: { actor_code: actor.code } });
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const addEmp = async () => {
    if (!form.code || !form.name) {
      toast.error("Code and name are required");
      return;
    }
    setBusy(true);
    try {
      // Non-owners can't set wages — always send zero so owner sets it later
      const payload = {
        ...form,
        hourly_rate: isOwner ? parseFloat(form.hourly_rate) || 0 : 0,
        weekly_salary: isOwner ? parseFloat(form.weekly_salary) || 0 : 0,
      };
      await axios.post(`${API}/timeclock/employees`, payload);
      toast.success(`Added ${form.name} (${form.pay_category === "cash" ? "Cash" : "Regular"})`);
      setShowAdd(false);
      setForm({ code: "", name: "", department: "Other", pay_type: "hourly", hourly_rate: 15, weekly_salary: 0, pay_category: "payroll" });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or code…"
          className="bg-[#252527] border-[#3a3a3c] text-white w-64 h-10"
          data-testid="emp-search"
        />
        <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="show-inactive" />
          Show inactive
        </label>
        <div className="flex-1" />
        <a href={`${API}/hr/kiosk-poster`} target="_blank" rel="noreferrer">
          <Button variant="outline" className="bg-[#252527] border-[#3a3a3c] text-gray-300 hover:bg-[#2f2f31]" data-testid="btn-qr-poster">
            🖨 Print QR poster
          </Button>
        </a>
        <a href="/hr/pto-sheet" target="_blank" rel="noreferrer">
          <Button variant="outline" className="bg-[#252527] border-[#3a3a3c] text-gray-300 hover:bg-[#2f2f31]" data-testid="btn-pto-sheet">
            🖨 PTO sheet
          </Button>
        </a>
        <Button onClick={() => setShowAdd(true)} className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="btn-add-emp">
          <UserPlus className="w-4 h-4 mr-1" /> Add employee
        </Button>
      </div>

      {showAdd && (
        <Card className="bg-[#1a1a1c] border-[#2a2a2c] p-4 mb-4" data-testid="add-emp-form">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Input placeholder="Code (e.g. 7870)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="bg-[#252527] border-[#3a3a3c] text-white" data-testid="add-code" />
            <Input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#252527] border-[#3a3a3c] text-white col-span-2" data-testid="add-name" />
            <select value={form.pay_category} onChange={(e) => setForm({ ...form, pay_category: e.target.value })} className="bg-[#252527] border border-[#3a3a3c] text-white rounded px-3 h-10 text-sm" data-testid="add-paycat">
              <option value="payroll">Regular (W-2)</option>
              <option value="cash">Cash</option>
            </select>
            <select value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="bg-[#252527] border border-[#3a3a3c] text-white rounded px-3 h-10 text-sm" data-testid="add-dept">
              {["Meat & Fish", "Produce", "Grocery", "Cashier", "Deli", "Frozen/Dairy", "Admin", "Other"].map((d) => <option key={d}>{d}</option>)}
            </select>
            <select value={form.pay_type} onChange={(e) => setForm({ ...form, pay_type: e.target.value })} className="bg-[#252527] border border-[#3a3a3c] text-white rounded px-3 h-10 text-sm">
              <option value="hourly">Hourly</option>
              <option value="salaried">Salaried</option>
            </select>
            {isOwner ? (
              form.pay_type === "hourly" ? (
                <Input type="number" step="0.25" placeholder="Hourly rate" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} className="bg-[#252527] border-[#3a3a3c] text-white" data-testid="add-rate" />
              ) : (
                <Input type="number" step="10" placeholder="Weekly salary" value={form.weekly_salary} onChange={(e) => setForm({ ...form, weekly_salary: e.target.value })} className="bg-[#252527] border-[#3a3a3c] text-white" data-testid="add-salary" />
              )
            ) : (
              <div className="text-[11px] text-gray-500 self-center italic" data-testid="wage-hidden-note">
                Wage set by owner
              </div>
            )}
            <div className="col-span-2 flex gap-2">
              <Button onClick={addEmp} disabled={busy} className="bg-green-500 hover:bg-green-600 text-white flex-1" data-testid="btn-save-emp">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
              </Button>
              <Button variant="ghost" onClick={() => setShowAdd(false)} className="text-gray-400">Cancel</Button>
            </div>
          </div>
        </Card>
      )}

      <Card className="bg-[#1a1a1c] border-[#2a2a2c] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#141416] text-[11px] uppercase tracking-wider text-gray-500">
            <tr>
              <th className="text-left px-3 py-2 w-16">Code</th>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Dept</th>
              {isOwner && <th className="text-right px-3 py-2">Pay</th>}
              <th className="text-center px-3 py-2 w-24">Status</th>
              <th className="w-32"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => (
              <tr key={e.code} className="border-t border-[#2a2a2c]" data-testid={`emp-${e.code}`}>
                <td className="px-3 py-2 font-mono text-xs text-gray-400">{e.code}</td>
                <td className="px-3 py-2 font-medium text-white">
                  {e.name}
                  {e.pay_category === "cash" && (
                    <Badge className="ml-2 bg-amber-500/20 text-amber-300 border-0 text-[9px] px-1.5 py-0">CASH</Badge>
                  )}
                </td>
                <td className="px-3 py-2 text-gray-400">{e.department}</td>
                {isOwner && (
                  <td className="px-3 py-2 text-right text-gray-400">
                    {e.pay_type === "salaried" ? `$${e.weekly_salary}/wk` : `$${e.hourly_rate}/hr`}
                  </td>
                )}
                <td className="px-3 py-2 text-center">
                  {e.active ? (
                    <Badge className="bg-green-500/20 text-green-300 border-0 text-[10px]">ACTIVE</Badge>
                  ) : (
                    <Badge className="bg-gray-600/40 text-gray-400 border-0 text-[10px]">INACTIVE</Badge>
                  )}
                </td>
                <td className="px-3 py-2 flex justify-end gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toggleActive(e.code, !e.active)}
                    className={`h-7 ${e.active ? "text-amber-300 hover:bg-amber-500/10" : "text-green-300 hover:bg-green-500/10"}`}
                    data-testid={`toggle-${e.code}`}
                    disabled={busy}
                  >
                    <Power className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteEmp(e.code, e.name)}
                    className="h-7 text-red-400 hover:bg-red-500/10"
                    data-testid={`del-${e.code}`}
                    disabled={busy}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="text-center text-gray-500 text-sm py-8">No employees match</div>}
      </Card>
    </>
  );
}

// ═══════════════════════════════════════════════════════════
// APPROVERS TAB — owner-only role management
// ═══════════════════════════════════════════════════════════
function ApproversTab({ actor }) {
  const [approvers, setApprovers] = useState([]);
  const [allEmps, setAllEmps] = useState([]);
  const [pickCode, setPickCode] = useState("");
  const [pickRole, setPickRole] = useState("hr_approver");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [a, e] = await Promise.all([
        axios.get(`${API}/hr/approvers`),
        axios.get(`${API}/timeclock/employees/all`),
      ]);
      setApprovers(a.data.approvers || []);
      setAllEmps(e.data || []);
    } catch {
      toast.error("Failed to load");
    }
  };
  useEffect(() => {
    load();
  }, []);

  const setRole = async (code, hr_role) => {
    setBusy(true);
    try {
      await axios.post(`${API}/hr/approvers`, {
        employee_code: code,
        hr_role,
        actor_code: actor.code,
      });
      toast.success("Updated");
      setPickCode("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const eligibleToAdd = allEmps
    .filter((e) => e.active && !e.is_test)
    .filter((e) => !approvers.some((a) => a.code === e.code))
    .sort((a, b) => a.name.localeCompare(b.name));

  const roleColor = (r) => ({
    owner: "bg-purple-500/20 text-purple-300",
    hr_manager: "bg-blue-500/20 text-blue-300",
    hr_approver: "bg-cyan-500/20 text-cyan-300",
  }[r] || "bg-gray-600/40 text-gray-300");

  return (
    <>
      <Card className="bg-[#1a1a1c] border-[#2a2a2c] p-4 mb-4" data-testid="add-approver-card">
        <h3 className="text-sm font-semibold mb-3">Grant HR role</h3>
        <div className="flex flex-wrap gap-2">
          <select value={pickCode} onChange={(e) => setPickCode(e.target.value)} className="bg-[#252527] border border-[#3a3a3c] text-white rounded px-3 h-10 text-sm flex-1 min-w-[200px]" data-testid="pick-emp">
            <option value="">Select employee…</option>
            {eligibleToAdd.map((e) => (
              <option key={e.code} value={e.code}>{e.name} (#{e.code})</option>
            ))}
          </select>
          <select value={pickRole} onChange={(e) => setPickRole(e.target.value)} className="bg-[#252527] border border-[#3a3a3c] text-white rounded px-3 h-10 text-sm" data-testid="pick-role">
            <option value="hr_approver">HR Approver (can approve/deny)</option>
            <option value="hr_manager">HR Manager (approve + edit employees)</option>
            <option value="owner">Owner (full access)</option>
          </select>
          <Button onClick={() => pickCode && setRole(pickCode, pickRole)} disabled={busy || !pickCode} className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="btn-grant">
            Grant
          </Button>
        </div>
      </Card>

      <Card className="bg-[#1a1a1c] border-[#2a2a2c] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#141416] text-[11px] uppercase tracking-wider text-gray-500">
            <tr>
              <th className="text-left px-3 py-2 w-16">Code</th>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Role</th>
              <th className="w-32"></th>
            </tr>
          </thead>
          <tbody>
            {approvers.map((a) => (
              <tr key={a.code} className="border-t border-[#2a2a2c]" data-testid={`app-${a.code}`}>
                <td className="px-3 py-2 font-mono text-xs text-gray-400">{a.code}</td>
                <td className="px-3 py-2 font-medium text-white">{a.name}</td>
                <td className="px-3 py-2">
                  <Badge className={`${roleColor(a.hr_role)} border-0 text-[10px]`}>
                    {a.hr_role.replace("_", " ").toUpperCase()}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-right">
                  {a.code !== actor.code && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setRole(a.code, "none")}
                      className="h-7 text-red-400 hover:bg-red-500/10 text-xs"
                      disabled={busy}
                      data-testid={`revoke-${a.code}`}
                    >
                      Revoke
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {approvers.length === 0 && <div className="text-center text-gray-500 text-sm py-8">No approvers yet</div>}
      </Card>
    </>
  );
}

// ═══════════════════════════════════════════════════════════
// CALENDAR TAB — month view of approved leave
// ═══════════════════════════════════════════════════════════
function CalendarTab() {
  const today = new Date();
  const [ym, setYM] = useState(
    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`
  );
  const [data, setData] = useState({ days: {}, count: 0 });
  const [loading, setLoading] = useState(true);

  const load = async (month) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/hr/calendar`, { params: { month } });
      setData(res.data);
    } catch {
      toast.error("Failed to load calendar");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load(ym);
  }, [ym]);

  const shift = (delta) => {
    const [y, m] = ym.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setYM(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  // Build calendar grid — 6 rows × 7 cols
  const [y, m] = ym.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const daysInMonth = new Date(y, m, 0).getDate();
  const startCol = first.getDay(); // 0=Sun
  const cells = [];
  for (let i = 0; i < startCol; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length < 42) cells.push(null);

  const monthName = first.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const todayISO = today.toISOString().slice(0, 10);

  return (
    <div data-testid="calendar-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <Button
          variant="ghost"
          onClick={() => shift(-1)}
          className="text-gray-400 hover:text-white"
          data-testid="cal-prev"
        >
          <ChevronLeft className="w-4 h-4" /> Prev
        </Button>
        <div className="text-center">
          <div className="text-xl font-bold">{monthName}</div>
          <div className="text-[11px] text-gray-500 mt-0.5">
            {data.count} leave-days scheduled
          </div>
        </div>
        <Button
          variant="ghost"
          onClick={() => shift(1)}
          className="text-gray-400 hover:text-white"
          data-testid="cal-next"
        >
          Next <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-3 text-[11px] text-gray-400">
        <LegendDot color="bg-green-500" label="Paid" />
        <LegendDot color="bg-gray-500" label="Unpaid" />
        <LegendDot color="bg-blue-500" label="Short-term" />
        <LegendDot color="bg-purple-500" label="Long-term" />
      </div>

      {/* Grid */}
      <Card className="bg-[#1a1a1c] border-[#2a2a2c] overflow-hidden">
        <div className="grid grid-cols-7 bg-[#141416] text-[10px] uppercase tracking-widest text-gray-500">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="px-2 py-2 text-center border-r border-[#2a2a2c] last:border-r-0">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((day, i) => {
            if (day === null) {
              return <div key={i} className="min-h-[90px] bg-[#141416]/50 border-r border-b border-[#2a2a2c] last:border-r-0" />;
            }
            const iso = `${ym}-${String(day).padStart(2, "0")}`;
            const rows = data.days[iso] || [];
            const isToday = iso === todayISO;
            return (
              <div
                key={i}
                className={`min-h-[90px] border-r border-b border-[#2a2a2c] last:border-r-0 p-1.5 relative
                  ${isToday ? "bg-blue-500/5" : ""}`}
                data-testid={`cal-day-${iso}`}
              >
                <div className={`text-[11px] font-semibold mb-1 ${isToday ? "text-blue-400" : "text-gray-500"}`}>
                  {day}
                  {rows.length >= 2 && (
                    <span className="ml-1 text-[9px] bg-red-500/20 text-red-300 px-1 rounded">
                      {rows.length} off
                    </span>
                  )}
                </div>
                <div className="space-y-0.5">
                  {rows.slice(0, 3).map((r, idx) => (
                    <div
                      key={idx}
                      title={`${r.employee_name} · ${r.request_type} · ${r.start_date} → ${r.end_date}`}
                      className={`text-[10px] px-1 py-0.5 rounded truncate ${typeBg(r.request_type)}`}
                    >
                      {r.employee_name}
                    </div>
                  ))}
                  {rows.length > 3 && (
                    <div className="text-[9px] text-gray-500 px-1">+{rows.length - 3} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {loading && (
        <div className="text-center text-xs text-gray-500 mt-3">
          <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> loading…
        </div>
      )}
    </div>
  );
}

function typeBg(t) {
  return (
    {
      paid: "bg-green-500/20 text-green-300",
      unpaid: "bg-gray-500/20 text-gray-300",
      short_term: "bg-blue-500/20 text-blue-300",
      long_term: "bg-purple-500/20 text-purple-300",
    }[t] || "bg-gray-500/20 text-gray-300"
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`inline-block w-2.5 h-2.5 rounded-full ${color}`} />
      {label}
    </span>
  );
}


// ═══════════════════════════════════════════════════════════
// Small helpers
// ═══════════════════════════════════════════════════════════
function TypeBadge({ type }) {
  const map = {
    paid: "bg-green-500/20 text-green-300",
    unpaid: "bg-gray-600/40 text-gray-300",
    short_term: "bg-blue-500/20 text-blue-300",
    long_term: "bg-purple-500/20 text-purple-300",
  };
  return (
    <Badge className={`${map[type] || map.paid} border-0 text-[10px]`}>
      {(type || "").replace("_", " ").toUpperCase()}
    </Badge>
  );
}
function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-500/20 text-amber-300",
    approved: "bg-green-500/20 text-green-300",
    denied: "bg-red-500/20 text-red-300",
  };
  return (
    <Badge className={`${map[status] || map.pending} border-0 text-[10px]`}>
      {(status || "").toUpperCase()}
    </Badge>
  );
}
